import { useCallback, useEffect, useState } from 'react';

import { DisclosurePanel, StageStatusBadge } from '../components/AppShellPrimitives';
import { sourceTypeLabel } from '../appHelpers';
import {
  EvidenceNeededGuidanceCard,
  buildRuntimeUploadEvidenceGuidance,
} from '../evidenceNeededGuidance';
import { formatEvidenceDateTime } from '../evidencePosture';
import {
  backfillSourceConnection,
  createEvaluationToolConnection,
  createEvaluationToolMapping,
  createExportDestination,
  createExternalLink,
  deleteExternalLink,
  fetchEvaluationToolConnectionSystems,
  fetchEvaluationToolConnections,
  fetchEvaluationToolMappings,
  fetchExportDestinations,
  fetchExternalLinks,
  fetchSourceConnections,
  replayIngestion,
  uploadRuntimeImport,
  updateEvaluationToolConnection,
  updateEvaluationToolMapping,
  updateExportDestination,
  updateExternalLink,
} from '../data';

function formatTimestamp(value) {
  if (!value) {
    return 'Not available';
  }
  return formatEvidenceDateTime(value);
}

function runtimeSourceWindowLabel(sourceConnection) {
  const windowMinutes = sourceConnection?.collection_window_minutes;
  if (typeof windowMinutes !== 'number' || windowMinutes <= 0) {
    return 'recent window';
  }
  if (windowMinutes % 1440 === 0) {
    const days = windowMinutes / 1440;
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }
  if (windowMinutes % 60 === 0) {
    const hours = windowMinutes / 60;
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }
  return `${windowMinutes} min`;
}

function formatSecretReference(secretProvider, secretRef) {
  if (!secretRef) {
    return 'Not configured';
  }
  if (!secretProvider) {
    return secretRef;
  }
  return secretRef.startsWith(`${secretProvider}:`) ? secretRef : `${secretProvider}:${secretRef}`;
}

function emptyExternalLinkForm() {
  return {
    mode: 'provider_preset',
    target_type: 'finding',
    link_kind: 'logs',
    provider: 'datadog',
    display_name: '',
    enabled: true,
    base_url: '',
    resolution_mode: 'template_url',
    url_value: '',
  };
}

function emptyExportDestinationForm() {
  return {
    destination_ref: '',
    display_name: '',
    base_url: '',
    project_key: '',
    issue_type: 'Task',
    email: '',
    secret_provider: 'env',
    secret_ref: '',
    enabled: true,
    secret_metadata_text: '',
  };
}

function emptyEvaluationToolConnectionForm() {
  return {
    connection_ref: '',
    display_name: '',
    api_base_url: '',
    environment_label: '',
    ase_workspace_id: '',
    ase_workspace_name: '',
    ase_workos_organization_id: '',
    client_id: '',
    client_secret: '',
    secret_provider: 'stored',
    secret_ref: '',
    secret_fingerprint: '',
    enabled: true,
    allowed_destination_systems_text: '',
  };
}

function emptyEvaluationToolMappingForm(connectionId = '') {
  return {
    evaluation_tool_connection_id: connectionId,
    ase_system_id: '',
    ase_system_display_name: '',
    status: 'confirmed',
    confidence: 'operator_confirmed',
  };
}

const RUNTIME_IMPORT_FORMAT_OPTIONS = [
  { value: 'smart_trace', label: 'Smart trace analysis (recommended)', accept: '.json,.jsonl,application/json,application/jsonl' },
  { value: 'runtime_json', label: 'Canonical Runtime JSON', accept: '.json,application/json' },
  { value: 'otel', label: 'OTEL JSON', accept: '.json,application/json' },
  { value: 'langsmith', label: 'LangSmith JSON', accept: '.json,application/json' },
  { value: 'langfuse', label: 'Langfuse JSON', accept: '.json,application/json' },
  { value: 'aws_xray', label: 'AWS X-Ray JSON', accept: '.json,application/json' },
  { value: 'jsonl', label: 'JSONL Trace Export', accept: '.jsonl,application/jsonl' },
  { value: 'agent_session_jsonl', label: 'Agent Session JSONL', accept: '.jsonl,application/jsonl' },
];

function runtimeImportFormatOption(sourceType) {
  return RUNTIME_IMPORT_FORMAT_OPTIONS.find((option) => option.value === sourceType) || RUNTIME_IMPORT_FORMAT_OPTIONS[0];
}

function formatCount(value) {
  return typeof value === 'number' ? value.toLocaleString() : 'Not available';
}

function formatConfidence(value) {
  return typeof value === 'number' ? `${Math.round(value * 100)}%` : 'Not available';
}

function componentGraphContextLabel(context) {
  switch (context?.mode) {
    case 'existing_component_graph':
      return 'Mapped using existing component graph';
    case 'clean_new_workflow':
      return 'Mapped from a clean/new workflow';
    default:
      return null;
  }
}

function formatTimeWindow(summary) {
  if (!summary?.time_range_start && !summary?.time_range_end) {
    return 'Not available';
  }
  if (summary.time_range_start && summary.time_range_end) {
    return `${formatTimestamp(summary.time_range_start)} to ${formatTimestamp(summary.time_range_end)}`;
  }
  return formatTimestamp(summary.time_range_start || summary.time_range_end);
}

const TRACE_ANALYSIS_STEPS = [
  'Upload received',
  'Reading structure',
  'Mapping trace shape',
  'Review confidence',
  'Ingesting evidence',
  'Guidance ready',
];

function TraceAnalysisStepper({ phase = 'idle' }) {
  const activeIndex = phase === 'complete' ? TRACE_ANALYSIS_STEPS.length - 1 : phase === 'analysing' ? 2 : phase === 'ready' ? 0 : -1;
  return (
    <ol className={`trace-analysis-stepper trace-analysis-stepper-${phase}`} aria-label="Smart trace analysis progress">
      {TRACE_ANALYSIS_STEPS.map((step, index) => {
        let state = 'pending';
        if (phase === 'complete' || index < activeIndex) {
          state = 'complete';
        } else if (index === activeIndex) {
          state = 'current';
        }
        return (
          <li className={`trace-analysis-step trace-analysis-step-${state}`} key={step}>
            <span aria-hidden="true">{index + 1}</span>
            <strong>{step}</strong>
          </li>
        );
      })}
    </ol>
  );
}

function TraceAnalysisSummary({ summary, mappingConfidence, semanticProfile, guidanceWarnings = [] }) {
  if (!summary && typeof mappingConfidence !== 'number' && !semanticProfile && !guidanceWarnings.length) {
    return null;
  }
  const warningItems = summary?.warnings?.length ? summary.warnings : guidanceWarnings;
  const unknownFields = summary?.unknown_fields || [];
  const confidentlyMapped = summary?.confidently_mapped || [];
  const detectedConventions = summary?.detected_conventions || [];
  const graphContextLabel = componentGraphContextLabel(summary?.component_graph_context);
  const mappingWarning = summary?.mapping_context_warning?.message;
  const duplicateWarning = summary?.duplicate_content_warning?.message;
  return (
    <div className="trace-analysis-summary" role="region" aria-label="Smart trace analysis preview">
      <div className="trace-analysis-summary-header">
        <div>
          <p className="detail-kicker">Smart trace analysis</p>
          <h4>Detected shape and mapping confidence</h4>
        </div>
        <span className="confidence-badge">{formatConfidence(mappingConfidence)}</span>
      </div>
      <div className="trace-analysis-metrics">
        <div>
          <span>Source shape</span>
          <strong>{summary?.raw_layout || 'Detected trace evidence'}</strong>
        </div>
        <div>
          <span>Records</span>
          <strong>{formatCount(summary?.record_count)}</strong>
        </div>
        <div>
          <span>Traces</span>
          <strong>{formatCount(summary?.trace_count)}</strong>
        </div>
        <div>
          <span>Spans</span>
          <strong>{formatCount(summary?.span_count)}</strong>
        </div>
        <div>
          <span>Model calls</span>
          <strong>{formatCount(summary?.model_call_candidates)}</strong>
        </div>
        <div>
          <span>Tool calls</span>
          <strong>{formatCount(summary?.tool_call_candidates)}</strong>
        </div>
      </div>
      <div className="trace-analysis-window">
        <span>Time window</span>
        <strong>{formatTimeWindow(summary)}</strong>
      </div>
      <div className="trace-analysis-chip-row" aria-label="Trace mapping details">
        {semanticProfile ? <span className="chip">{semanticProfile}</span> : null}
        {summary?.mapping_fingerprint?.fingerprint ? <span className="chip chip-success">Mapping fingerprint saved</span> : null}
        {graphContextLabel ? <span className="chip chip-success">{graphContextLabel}</span> : null}
        {detectedConventions.map((item) => (
          <span className="chip chip-success" key={`convention-${item}`}>{item}</span>
        ))}
        {confidentlyMapped.slice(0, 3).map((item) => (
          <span className="chip chip-success" key={`mapped-${item}`}>{item}</span>
        ))}
        {typeof summary?.unknown_field_count === 'number' ? (
          <span className={summary.unknown_field_count > 0 ? 'chip chip-warning' : 'chip chip-success'}>
            {summary.unknown_field_count} unknown fields
          </span>
        ) : null}
      </div>
      {warningItems.length ? (
        <div className="runtime-upload-warning-list" aria-label="Trace analysis warnings">
          {warningItems.slice(0, 3).map((warning) => (
            <span className="chip chip-warning" key={warning}>{warning}</span>
          ))}
        </div>
      ) : null}
      {mappingWarning || duplicateWarning ? (
        <div className="runtime-upload-warning-list" aria-label="Trace mapping context warnings">
          {mappingWarning ? <span className="chip chip-warning">{mappingWarning}</span> : null}
          {duplicateWarning ? <span className="chip chip-warning">{duplicateWarning}</span> : null}
        </div>
      ) : null}
      {unknownFields.length ? (
        <DisclosurePanel title="Unknown fields" summaryMeta={`${unknownFields.length} shown`}>
          <div className="trace-analysis-field-list">
            {unknownFields.map((field) => (
              <span className="chip" key={field}>{field}</span>
            ))}
          </div>
        </DisclosurePanel>
      ) : null}
    </div>
  );
}

function RuntimeUploadStatusCard({ feedback }) {
  if (!feedback) {
    return null;
  }
  const toneLabel = feedback.deduped ? 'Existing evidence reused' : 'Runtime evidence added';
  const detailItems = [
    ['Workflow', feedback.workflowName],
    ['Format', feedback.detectedFormat || feedback.formatLabel],
    ['Uploaded', formatTimestamp(feedback.uploadedAt)],
    typeof feedback.mappingConfidence === 'number' ? ['Confidence', `${Math.round(feedback.mappingConfidence * 100)}%`] : null,
  ].filter(Boolean);
  const warnings = [
    ...(feedback.guidanceWarnings || []),
    feedback.mappingContextWarning?.message,
    feedback.duplicateContentWarning?.message,
  ].filter(Boolean);

  return (
    <section className="status-card runtime-import-status-card" role="status" aria-label="Runtime upload status">
      <div className="runtime-import-status-header">
        <div>
          <p className="detail-kicker">{toneLabel}</p>
          <h4>{feedback.title}</h4>
        </div>
        <span className="chip chip-success">{feedback.status || 'registered'}</span>
      </div>
      <div className="runtime-import-status-grid">
        {detailItems.map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{value || 'Not available'}</strong>
          </div>
        ))}
      </div>
      {feedback.semanticProfile || feedback.componentGraphContext?.mode || feedback.mappingFingerprint?.fingerprint ? (
        <div className="trace-analysis-chip-row" aria-label="Runtime upload mapping context">
          {feedback.semanticProfile ? <span className="chip">{feedback.semanticProfile}</span> : null}
          {feedback.componentGraphContext?.mode ? <span className="chip chip-success">{componentGraphContextLabel(feedback.componentGraphContext)}</span> : null}
          {feedback.mappingFingerprint?.fingerprint ? <span className="chip chip-success">Mapping fingerprint saved</span> : null}
        </div>
      ) : null}
      {warnings.length ? (
        <div className="runtime-upload-warning-list" aria-label="Runtime upload warnings">
          {warnings.slice(0, 3).map((warning) => (
            <span className="chip chip-warning" key={warning}>{warning}</span>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function buildRuntimeImportFeedback({ result, file, workflowName, formatLabel, reusedSourceConnection }) {
  let title;
  if (result?.deduped) {
    title = 'Upload reused';
  } else if (reusedSourceConnection) {
    title = 'Upload appended';
  } else {
    title = 'Upload accepted';
  }
  const analysisSummary = result?.trace_analysis_summary
    ? {
        ...result.trace_analysis_summary,
        mapping_fingerprint: result?.mapping_fingerprint || result.trace_analysis_summary.mapping_fingerprint,
        mapping_context_warning: result?.mapping_context_warning || result.trace_analysis_summary.mapping_context_warning,
        duplicate_content_warning: result?.duplicate_content_warning || result.trace_analysis_summary.duplicate_content_warning,
        component_graph_context: result?.component_graph_context || result.trace_analysis_summary.component_graph_context,
      }
    : null;
  return {
    title,
    fileName: file?.name || 'Uploaded trace file',
    workflowName,
    formatLabel,
    uploadedAt: new Date().toISOString(),
    ingestionId: result?.ingestion_id || null,
    status: result?.status || null,
    detectedFormat: result?.detected_format || null,
    detectedVariant: result?.detected_variant || null,
    mappingConfidence: result?.mapping_confidence ?? null,
    semanticProfile: result?.semantic_profile || null,
    analysisSummary,
    mappingFingerprint: result?.mapping_fingerprint || null,
    mappingContextWarning: result?.mapping_context_warning || null,
    duplicateContentWarning: result?.duplicate_content_warning || null,
    componentGraphContext: result?.component_graph_context || null,
    guidanceWarnings: result?.guidance_warnings || [],
    deduped: Boolean(result?.deduped),
  };
}

function externalLinkFormFromDefinition(definition) {
  return {
    mode: definition.metadata?.preset_mode === 'provider_preset' ? 'provider_preset' : 'custom',
    target_type: definition.target_type,
    link_kind: definition.link_kind,
    provider: definition.provider,
    display_name: definition.metadata?.display_name_mode === 'custom' ? definition.display_name : '',
    enabled: Boolean(definition.enabled),
    base_url: definition.metadata?.base_url || '',
    resolution_mode: definition.resolution_mode,
    url_value: definition.url_value || '',
  };
}

function exportDestinationFormFromDestination(destination) {
  return {
    destination_ref: destination.destination_ref || '',
    display_name: destination.display_name || '',
    base_url: destination.base_url || '',
    project_key: destination.project_key || '',
    issue_type: destination.issue_type || 'Task',
    email: destination.email || '',
    secret_provider: destination.secret_provider || 'env',
    secret_ref: destination.secret_ref || '',
    enabled: Boolean(destination.enabled),
    secret_metadata_text: destination.secret_metadata ? JSON.stringify(destination.secret_metadata, null, 2) : '',
  };
}

function evaluationConnectionFormFromConnection(connection) {
  return {
    connection_ref: connection.connection_ref || '',
    display_name: connection.display_name || '',
    api_base_url: connection.api_base_url || '',
    environment_label: connection.environment_label || '',
    ase_workspace_id: connection.ase_workspace_id || '',
    ase_workspace_name: connection.ase_workspace_name || '',
    ase_workos_organization_id: connection.ase_workos_organization_id || '',
    client_id: connection.client_id || '',
    client_secret: '',
    secret_provider: connection.secret_provider || 'stored',
    secret_ref: connection.secret_ref || '',
    secret_fingerprint: connection.secret_fingerprint || '',
    enabled: Boolean(connection.enabled),
    allowed_destination_systems_text: (connection.allowed_destination_systems || [])
      .map((item) => [item.ase_system_id, item.display_name].filter(Boolean).join(' | '))
      .join('\n'),
  };
}

function evaluationMappingFormFromMapping(mapping) {
  return {
    evaluation_tool_connection_id: mapping.evaluation_tool_connection_id || '',
    ase_system_id: mapping.ase_system_id || '',
    ase_system_display_name: mapping.ase_system_display_name || '',
    status: mapping.status || 'confirmed',
    confidence: mapping.confidence || 'operator_confirmed',
  };
}

function buildExternalLinkPayload(form, { isUpdate = false } = {}) {
  const payload = {
    target_type: form.target_type,
    link_kind: form.link_kind,
    provider: form.provider,
    enabled: Boolean(form.enabled),
  };

  if (form.mode === 'provider_preset') {
    payload.metadata = form.base_url ? { base_url: form.base_url.trim() } : {};
    if (form.display_name.trim()) {
      payload.display_name = form.display_name.trim();
    } else if (isUpdate) {
      payload.display_name = null;
    }
  } else {
    payload.display_name = form.display_name.trim();
    payload.resolution_mode = form.resolution_mode;
    payload.url_value = form.url_value.trim();
  }

  return payload;
}

function buildExportDestinationPayload(form, { includeRef = true, includeType = true } = {}) {
  let secretMetadata;
  if (form.secret_metadata_text.trim()) {
    secretMetadata = JSON.parse(form.secret_metadata_text);
  }

  const payload = {
    display_name: form.display_name.trim(),
    base_url: form.base_url.trim(),
    project_key: form.project_key.trim(),
    issue_type: form.issue_type.trim(),
    email: form.email.trim(),
    secret_provider: form.secret_provider.trim() || null,
    secret_ref: form.secret_ref.trim() || null,
    secret_metadata: secretMetadata,
    enabled: Boolean(form.enabled),
  };

  if (includeType) {
    payload.destination_type = 'jira';
  }
  if (includeRef) {
    payload.destination_ref = form.destination_ref.trim();
  }

  return payload;
}

function parseAllowedDestinationSystems(value) {
  return String(value || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [aseSystemId, displayName] = line.split('|').map((part) => part.trim());
      return { ase_system_id: aseSystemId, display_name: displayName || null };
    })
    .filter((item) => item.ase_system_id);
}

function isLocalAseUrl(value) {
  const normalized = String(value || '').toLowerCase();
  return normalized.includes('localhost') || normalized.includes('127.0.0.1');
}

function deriveEvaluationConnectionFormValues(form) {
  const apiBaseUrl = form.api_base_url.trim();
  const clientId = form.client_id.trim();
  const local = isLocalAseUrl(apiBaseUrl);
  const environmentLabel = form.environment_label.trim() || (local ? 'local' : 'production');
  const displayName = form.display_name.trim() || (local ? 'Local ASE' : 'Automated-System-Eval');
  const connectionRef = form.connection_ref.trim() || (local ? 'ase_local' : 'ase_primary');
  return {
    connectionRef,
    displayName,
    environmentLabel,
    workspaceId: form.ase_workspace_id.trim() || clientId || connectionRef,
    workspaceName: form.ase_workspace_name.trim() || displayName,
  };
}

function buildEvaluationToolConnectionPayload(form, { includeRef = true } = {}) {
  const derived = deriveEvaluationConnectionFormValues(form);
  const payload = {
    display_name: derived.displayName,
    ase_workspace_id: derived.workspaceId,
    ase_workspace_name: derived.workspaceName || null,
    ase_workos_organization_id: form.ase_workos_organization_id.trim() || null,
    api_base_url: form.api_base_url.trim(),
    environment_label: derived.environmentLabel,
    auth_mode: 'client_credentials',
    client_id: form.client_id.trim(),
    enabled: Boolean(form.enabled),
    allowed_destination_systems: parseAllowedDestinationSystems(form.allowed_destination_systems_text),
  };
  if (form.client_secret.trim()) {
    payload.client_secret = form.client_secret.trim();
  } else if (form.secret_provider.trim() && form.secret_ref.trim() && form.secret_provider.trim() !== 'stored') {
    payload.secret_provider = form.secret_provider.trim();
    payload.secret_ref = form.secret_ref.trim();
  }
  if (includeRef) {
    payload.connection_ref = derived.connectionRef;
  }
  return payload;
}

function matchingEvaluationToolConnection(connections, form) {
  const derived = deriveEvaluationConnectionFormValues(form);
  return (connections || []).find((connection) => connection.connection_ref === derived.connectionRef) || null;
}

function buildEvaluationToolMappingPayload(form, { includeConnection = true } = {}) {
  const payload = {
    ase_system_id: form.ase_system_id.trim(),
    ase_system_display_name: form.ase_system_display_name.trim(),
    status: form.status,
    confidence: form.confidence.trim() || 'operator_confirmed',
  };
  if (includeConnection) {
    payload.evaluation_tool_connection_id = form.evaluation_tool_connection_id;
  }
  return payload;
}

function chipToneClass(status) {
  if (status === 'available' || status === 'succeeded' || status === 'active' || status === 'healthy') {
    return 'chip chip-success';
  }
  if (status === 'disabled' || status === 'failed' || status === 'stale') {
    return 'chip chip-alert';
  }
  return 'chip';
}

function statusNeedsAttention(status) {
  if (!status) {
    return false;
  }
  return ['disabled', 'failed', 'misconfigured', 'stale'].includes(String(status).toLowerCase());
}

function onOffChipClass(enabled) {
  return enabled ? 'chip chip-success' : 'chip chip-alert';
}

function ExternalLinkEditor({ title, form, setForm, onSubmit, submitLabel, loading, error, submitDisabled = false }) {
  return (
    <form
      className="form-section"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="form-section-header">
        <h3>{title}</h3>
        <p>Preset-backed links are best for supported providers. Custom mode keeps the link fully explicit.</p>
      </div>

      <label className="form-field">
        <span>Definition mode</span>
        <select className="text-input" value={form.mode} onChange={(event) => setForm((current) => ({ ...current, mode: event.target.value }))}>
          <option value="provider_preset">Provider preset</option>
          <option value="custom">Custom link</option>
        </select>
      </label>

      <div className="access-grid">
        <label className="form-field">
          <span>Target type</span>
          <select className="text-input" value={form.target_type} onChange={(event) => setForm((current) => ({ ...current, target_type: event.target.value }))}>
            <option value="finding">Finding</option>
            <option value="workflow">Workflow</option>
            <option value="component">Component</option>
            <option value="run">Run</option>
          </select>
        </label>
        <label className="form-field">
          <span>Link kind</span>
          <select className="text-input" value={form.link_kind} onChange={(event) => setForm((current) => ({ ...current, link_kind: event.target.value }))}>
            <option value="logs">Logs</option>
            <option value="trace">Trace</option>
            <option value="dashboard">Dashboard</option>
            <option value="runbook">Runbook</option>
            <option value="incident_context">Incident context</option>
          </select>
        </label>
      </div>

      <div className="access-grid">
        <label className="form-field">
          <span>Provider</span>
          <input className="text-input" value={form.provider} onChange={(event) => setForm((current) => ({ ...current, provider: event.target.value }))} />
        </label>
        <label className="form-field">
          <span>Display name</span>
          <input
            className="text-input"
            placeholder={form.mode === 'provider_preset' ? 'Leave blank to use preset label' : 'Required'}
            value={form.display_name}
            onChange={(event) => setForm((current) => ({ ...current, display_name: event.target.value }))}
          />
        </label>
      </div>

      {form.mode === 'provider_preset' ? (
        <label className="form-field">
          <span>Provider base URL</span>
          <input
            className="text-input"
            placeholder="https://app.datadoghq.com"
            value={form.base_url}
            onChange={(event) => setForm((current) => ({ ...current, base_url: event.target.value }))}
          />
        </label>
      ) : (
        <div className="access-grid">
          <label className="form-field">
            <span>Resolution mode</span>
            <select
              className="text-input"
              value={form.resolution_mode}
              onChange={(event) => setForm((current) => ({ ...current, resolution_mode: event.target.value }))}
            >
              <option value="template_url">Template URL</option>
              <option value="static_url">Static URL</option>
            </select>
          </label>
          <label className="form-field">
            <span>URL value</span>
            <input className="text-input" value={form.url_value} onChange={(event) => setForm((current) => ({ ...current, url_value: event.target.value }))} />
          </label>
        </div>
      )}

      <label className="checkbox-row">
        <input checked={form.enabled} type="checkbox" onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))} />
        <span>Enabled</span>
      </label>

      {error ? <p>{error}</p> : null}
      <div className="form-actions">
        <button className="primary-button" type="submit" disabled={loading || submitDisabled}>
          {loading ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

function ExportDestinationEditor({ title, form, setForm, onSubmit, submitLabel, loading, error, submitDisabled = false }) {
  return (
    <form
      className="form-section"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="form-section-header">
        <h3>{title}</h3>
        <p>Managed destinations are tenant-scoped and reference secrets by pointer only. Inline credentials are not accepted.</p>
      </div>

      <div className="access-grid">
        <label className="form-field">
          <span>Destination ref</span>
          <input
            className="text-input"
            value={form.destination_ref}
            onChange={(event) => setForm((current) => ({ ...current, destination_ref: event.target.value }))}
          />
        </label>
        <label className="form-field">
          <span>Display name</span>
          <input
            className="text-input"
            value={form.display_name}
            onChange={(event) => setForm((current) => ({ ...current, display_name: event.target.value }))}
          />
        </label>
      </div>

      <div className="access-grid">
        <label className="form-field">
          <span>Jira base URL</span>
          <input className="text-input" value={form.base_url} onChange={(event) => setForm((current) => ({ ...current, base_url: event.target.value }))} />
        </label>
        <label className="form-field">
          <span>Project key</span>
          <input className="text-input" value={form.project_key} onChange={(event) => setForm((current) => ({ ...current, project_key: event.target.value }))} />
        </label>
      </div>

      <div className="access-grid">
        <label className="form-field">
          <span>Issue type</span>
          <input className="text-input" value={form.issue_type} onChange={(event) => setForm((current) => ({ ...current, issue_type: event.target.value }))} />
        </label>
        <label className="form-field">
          <span>Automation email</span>
          <input className="text-input" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} />
        </label>
      </div>

      <div className="access-grid">
        <label className="form-field">
          <span>Secret provider</span>
          <input
            className="text-input"
            value={form.secret_provider}
            onChange={(event) => setForm((current) => ({ ...current, secret_provider: event.target.value }))}
          />
        </label>
        <label className="form-field">
          <span>Secret reference</span>
          <input className="text-input" value={form.secret_ref} onChange={(event) => setForm((current) => ({ ...current, secret_ref: event.target.value }))} />
        </label>
      </div>

      <label className="form-field">
        <span>Secret metadata (JSON, optional)</span>
        <textarea
          className="text-input"
          rows={4}
          value={form.secret_metadata_text}
          onChange={(event) => setForm((current) => ({ ...current, secret_metadata_text: event.target.value }))}
        />
      </label>

      <label className="checkbox-row">
        <input checked={form.enabled} type="checkbox" onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))} />
        <span>Enabled</span>
      </label>

      {error ? <p>{error}</p> : null}
      <div className="form-actions">
        <button className="primary-button" type="submit" disabled={loading || submitDisabled}>
          {loading ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

function EvaluationToolConnectionEditor({ title, form, setForm, onSubmit, submitLabel, loading, error, submitDisabled = false, readOnlyRef = false }) {
  return (
    <form
      className="form-section"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="form-section-header">
        <h3>{title}</h3>
        <p>Automated-System-Eval connections are tenant-scoped and use ASE client credentials stored server-side.</p>
      </div>

      <div className="access-grid">
        <label className="form-field">
          <span>ASE base URL</span>
          <input
            className="text-input"
            required
            placeholder="https://ase.your-company.com"
            value={form.api_base_url}
            onChange={(event) => setForm((current) => ({ ...current, api_base_url: event.target.value }))}
          />
        </label>
        <label className="form-field">
          <span>ASE client id</span>
          <input
            className="text-input"
            required
            placeholder="ase_ic_..."
            value={form.client_id}
            onChange={(event) => setForm((current) => ({ ...current, client_id: event.target.value }))}
          />
        </label>
      </div>

      <label className="form-field">
        <span>ASE client secret</span>
        <input
          className="text-input"
          required={!readOnlyRef && !form.secret_fingerprint}
          type="password"
          autoComplete="off"
          placeholder={readOnlyRef || form.secret_fingerprint ? 'Leave blank to keep stored secret' : 'ase_cs_...'}
          value={form.client_secret}
          onChange={(event) => setForm((current) => ({ ...current, client_secret: event.target.value }))}
        />
      </label>
      {form.secret_fingerprint ? <p className="overview-note">Stored secret fingerprint: {form.secret_fingerprint}</p> : null}

      <details className="form-section-details">
        <summary>Advanced connection details</summary>

        <div className="access-grid">
          <label className="form-field">
            <span>Connection ref</span>
            <input
              className="text-input"
              value={form.connection_ref}
              disabled={readOnlyRef}
              placeholder={isLocalAseUrl(form.api_base_url) ? 'ase_local' : 'ase_primary'}
              onChange={(event) => setForm((current) => ({ ...current, connection_ref: event.target.value }))}
            />
          </label>
          <label className="form-field">
            <span>Display name</span>
            <input
              className="text-input"
              value={form.display_name}
              placeholder={isLocalAseUrl(form.api_base_url) ? 'Local ASE' : 'Automated-System-Eval'}
              onChange={(event) => setForm((current) => ({ ...current, display_name: event.target.value }))}
            />
          </label>
        </div>

        <div className="access-grid">
          <label className="form-field">
            <span>ASE workspace id</span>
            <input className="text-input" value={form.ase_workspace_id} placeholder="Uses ASE client id when blank" onChange={(event) => setForm((current) => ({ ...current, ase_workspace_id: event.target.value }))} />
          </label>
          <label className="form-field">
            <span>ASE workspace name</span>
            <input className="text-input" value={form.ase_workspace_name} placeholder="Uses display name when blank" onChange={(event) => setForm((current) => ({ ...current, ase_workspace_name: event.target.value }))} />
          </label>
        </div>

        <div className="access-grid">
          <label className="form-field">
            <span>Environment</span>
            <input className="text-input" value={form.environment_label} placeholder={isLocalAseUrl(form.api_base_url) ? 'local' : 'production'} onChange={(event) => setForm((current) => ({ ...current, environment_label: event.target.value }))} />
          </label>
          <label className="form-field">
            <span>ASE WorkOS org id</span>
            <input
              className="text-input"
              value={form.ase_workos_organization_id}
              onChange={(event) => setForm((current) => ({ ...current, ase_workos_organization_id: event.target.value }))}
            />
          </label>
        </div>

        <label className="form-field">
          <span>Allowed ASE systems</span>
          <textarea
            className="text-input"
            rows={3}
            value={form.allowed_destination_systems_text}
            placeholder="Leave blank; ASE token allowlist is enforced by ASE"
            onChange={(event) => setForm((current) => ({ ...current, allowed_destination_systems_text: event.target.value }))}
          />
        </label>
      </details>

      <label className="checkbox-row">
        <input checked={form.enabled} type="checkbox" onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))} />
        <span>Enabled</span>
      </label>

      {error ? <p>{error}</p> : null}
      <div className="form-actions">
        <button className="primary-button" type="submit" disabled={loading || submitDisabled}>
          {loading ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

function EvaluationToolMappingEditor({
  title,
  form,
  setForm,
  connections,
  destinationSystems = [],
  destinationSystemsLoading = false,
  destinationSystemsError = '',
  onRefreshSystems,
  onSubmit,
  submitLabel,
  loading,
  error,
  submitDisabled = false,
  readOnlyConnection = false,
}) {
  const selectedSystemMissing = form.ase_system_id && !destinationSystems.some((system) => system.ase_system_id === form.ase_system_id);
  const systemSelectDisabled = destinationSystemsLoading || (!destinationSystems.length && !selectedSystemMissing);
  return (
    <form
      className="form-section"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="form-section-header">
        <h3>{title}</h3>
        <p>Map this Agentic Blue system to the ASE system operators should review.</p>
      </div>

      <div className="access-grid">
        <label className="form-field">
          <span>ASE connection</span>
          <select
            className="text-input"
            value={form.evaluation_tool_connection_id}
            disabled={readOnlyConnection}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                evaluation_tool_connection_id: event.target.value,
                ase_system_id: '',
                ase_system_display_name: '',
              }))
            }
          >
            {connections.length ? null : <option value="">No ASE connections configured</option>}
            {connections.map((connection) => (
              <option key={connection.id} value={connection.id}>
                {connection.display_name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="form-field">
        <span>ASE system</span>
        <select
          className="text-input"
          required
          disabled={systemSelectDisabled}
          value={form.ase_system_id}
          onChange={(event) => {
            const selected = destinationSystems.find((system) => system.ase_system_id === event.target.value);
            setForm((current) => ({
              ...current,
              ase_system_id: event.target.value,
              ase_system_display_name: selected?.display_name || selected?.ase_system_id || '',
            }));
          }}
        >
          <option value="">{destinationSystemsLoading ? 'Loading ASE systems…' : 'Choose ASE system'}</option>
          {selectedSystemMissing ? <option value={form.ase_system_id}>{form.ase_system_display_name || form.ase_system_id}</option> : null}
          {destinationSystems.map((system) => (
            <option key={system.ase_system_id} value={system.ase_system_id}>
              {system.display_name || system.ase_system_id}
            </option>
          ))}
        </select>
      </label>
      {destinationSystemsError ? (
        <p>
          {destinationSystemsError}
          {onRefreshSystems ? (
            <>
              {' '}
              <button className="secondary-button" type="button" onClick={onRefreshSystems}>
                Retry systems
              </button>
            </>
          ) : null}
        </p>
      ) : null}
      <input
        type="hidden"
        value={form.ase_system_display_name}
        readOnly
      />

      {error ? <p>{error}</p> : null}
      <div className="form-actions">
        <button className="primary-button" type="submit" disabled={loading || submitDisabled || !form.ase_system_id}>
          {loading ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

export default function IntegrationsView({ selectedSystem, activeRole, canOperateRuntimeSources, workflowSummaries = [] }) {
  const canManageIntegrations = activeRole === 'tenant_admin';
  const accessLabel = canManageIntegrations ? 'Admin' : canOperateRuntimeSources ? 'Operator' : 'Read-only';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [sourceConnections, setSourceConnections] = useState([]);
  const [externalLinks, setExternalLinks] = useState([]);
  const [exportDestinations, setExportDestinations] = useState([]);
  const [evaluationToolConnections, setEvaluationToolConnections] = useState([]);
  const [evaluationToolMappings, setEvaluationToolMappings] = useState([]);
  const [runtimeActionLoadingRef, setRuntimeActionLoadingRef] = useState(null);
  const [createExternalLinkForm, setCreateExternalLinkForm] = useState(() => emptyExternalLinkForm());
  const [createExternalLinkLoading, setCreateExternalLinkLoading] = useState(false);
  const [createExternalLinkError, setCreateExternalLinkError] = useState('');
  const [editingExternalLinkId, setEditingExternalLinkId] = useState(null);
  const [editingExternalLinkForm, setEditingExternalLinkForm] = useState(() => emptyExternalLinkForm());
  const [editingExternalLinkLoading, setEditingExternalLinkLoading] = useState(false);
  const [editingExternalLinkError, setEditingExternalLinkError] = useState('');
  const [createExportDestinationForm, setCreateExportDestinationForm] = useState(() => emptyExportDestinationForm());
  const [createExportDestinationLoading, setCreateExportDestinationLoading] = useState(false);
  const [createExportDestinationError, setCreateExportDestinationError] = useState('');
  const [editingExportDestinationId, setEditingExportDestinationId] = useState(null);
  const [editingExportDestinationForm, setEditingExportDestinationForm] = useState(() => emptyExportDestinationForm());
  const [editingExportDestinationLoading, setEditingExportDestinationLoading] = useState(false);
  const [editingExportDestinationError, setEditingExportDestinationError] = useState('');
  const [createEvaluationConnectionForm, setCreateEvaluationConnectionForm] = useState(() => emptyEvaluationToolConnectionForm());
  const [createEvaluationConnectionLoading, setCreateEvaluationConnectionLoading] = useState(false);
  const [createEvaluationConnectionError, setCreateEvaluationConnectionError] = useState('');
  const [editingEvaluationConnectionId, setEditingEvaluationConnectionId] = useState(null);
  const [editingEvaluationConnectionForm, setEditingEvaluationConnectionForm] = useState(() => emptyEvaluationToolConnectionForm());
  const [editingEvaluationConnectionLoading, setEditingEvaluationConnectionLoading] = useState(false);
  const [editingEvaluationConnectionError, setEditingEvaluationConnectionError] = useState('');
  const [createEvaluationMappingForm, setCreateEvaluationMappingForm] = useState(() => emptyEvaluationToolMappingForm());
  const [createEvaluationMappingLoading, setCreateEvaluationMappingLoading] = useState(false);
  const [createEvaluationMappingError, setCreateEvaluationMappingError] = useState('');
  const [editingEvaluationMappingId, setEditingEvaluationMappingId] = useState(null);
  const [editingEvaluationMappingForm, setEditingEvaluationMappingForm] = useState(() => emptyEvaluationToolMappingForm());
  const [editingEvaluationMappingLoading, setEditingEvaluationMappingLoading] = useState(false);
  const [editingEvaluationMappingError, setEditingEvaluationMappingError] = useState('');
  const [evaluationDestinationSystemsByConnection, setEvaluationDestinationSystemsByConnection] = useState({});
  const [evaluationDestinationSystemsLoadingByConnection, setEvaluationDestinationSystemsLoadingByConnection] = useState({});
  const [evaluationDestinationSystemsErrorByConnection, setEvaluationDestinationSystemsErrorByConnection] = useState({});
  const [runtimeImportWorkflowId, setRuntimeImportWorkflowId] = useState('');
  const [runtimeImportSourceType, setRuntimeImportSourceType] = useState('smart_trace');
  const [runtimeImportFile, setRuntimeImportFile] = useState(null);
  const [runtimeImportFileInputKey, setRuntimeImportFileInputKey] = useState(0);
  const [runtimeImportFeedback, setRuntimeImportFeedback] = useState(null);
  const [runtimeImportLoading, setRuntimeImportLoading] = useState(false);
  const [runtimeImportError, setRuntimeImportError] = useState('');

  const loadEvaluationDestinationSystems = useCallback(async (connectionId) => {
    if (!connectionId) {
      return;
    }
    setEvaluationDestinationSystemsLoadingByConnection((current) => ({ ...current, [connectionId]: true }));
    setEvaluationDestinationSystemsErrorByConnection((current) => ({ ...current, [connectionId]: '' }));
    try {
      const systems = await fetchEvaluationToolConnectionSystems(connectionId);
      setEvaluationDestinationSystemsByConnection((current) => ({ ...current, [connectionId]: systems }));
    } catch (requestError) {
      setEvaluationDestinationSystemsErrorByConnection((current) => ({ ...current, [connectionId]: requestError.message }));
      setEvaluationDestinationSystemsByConnection((current) => ({ ...current, [connectionId]: [] }));
    } finally {
      setEvaluationDestinationSystemsLoadingByConnection((current) => ({ ...current, [connectionId]: false }));
    }
  }, []);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const [
          sourceConnectionsPayload,
          externalLinksPayload,
          exportDestinationsPayload,
          evaluationToolConnectionsPayload,
          evaluationToolMappingsPayload,
        ] = await Promise.all([
          fetchSourceConnections(selectedSystem.id),
          fetchExternalLinks(selectedSystem.id),
          fetchExportDestinations(),
          fetchEvaluationToolConnections(),
          fetchEvaluationToolMappings(selectedSystem.id),
        ]);
        if (!active) {
          return;
        }
        setSourceConnections(sourceConnectionsPayload);
        setExternalLinks(externalLinksPayload);
        setExportDestinations(exportDestinationsPayload);
        setEvaluationToolConnections(evaluationToolConnectionsPayload);
        setEvaluationToolMappings(evaluationToolMappingsPayload);
      } catch (requestError) {
        if (!active) {
          return;
        }
        setError(requestError.message);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [selectedSystem.id, refreshKey]);

  useEffect(() => {
    const workflowOptions = workflowSummaries.map((summary) => summary.workflow).filter(Boolean);
    if (!workflowOptions.length) {
      setRuntimeImportWorkflowId('');
      return;
    }
    setRuntimeImportWorkflowId((current) =>
      workflowOptions.some((workflow) => workflow.id === current) ? current : workflowOptions[0].id
    );
  }, [workflowSummaries]);

  useEffect(() => {
    setCreateEvaluationMappingForm((current) => {
      const firstConnection = evaluationToolConnections.find((connection) => connection?.id);
      if (!firstConnection) {
        return current.evaluation_tool_connection_id ? { ...current, evaluation_tool_connection_id: '' } : current;
      }
      if (evaluationToolConnections.some((connection) => connection.id === current.evaluation_tool_connection_id)) {
        return current;
      }
      return { ...current, evaluation_tool_connection_id: firstConnection.id };
    });
  }, [evaluationToolConnections]);

  useEffect(() => {
    if (canManageIntegrations && createEvaluationMappingForm.evaluation_tool_connection_id) {
      loadEvaluationDestinationSystems(createEvaluationMappingForm.evaluation_tool_connection_id);
    }
  }, [canManageIntegrations, createEvaluationMappingForm.evaluation_tool_connection_id, loadEvaluationDestinationSystems]);

  useEffect(() => {
    if (canManageIntegrations && editingEvaluationMappingId && editingEvaluationMappingForm.evaluation_tool_connection_id) {
      loadEvaluationDestinationSystems(editingEvaluationMappingForm.evaluation_tool_connection_id);
    }
  }, [canManageIntegrations, editingEvaluationMappingForm.evaluation_tool_connection_id, editingEvaluationMappingId, loadEvaluationDestinationSystems]);

  const refresh = () => setRefreshKey((current) => current + 1);
  const workflowOptions = workflowSummaries.map((summary) => summary.workflow).filter(Boolean);
  const hasEvaluationToolConnections = evaluationToolConnections.some((connection) => connection?.id);
  const selectedRuntimeImportWorkflow = workflowOptions.find((workflow) => workflow.id === runtimeImportWorkflowId) || null;
  const selectedRuntimeImportFormat = runtimeImportFormatOption(runtimeImportSourceType);
  const runtimeUploadGuidance = buildRuntimeUploadEvidenceGuidance({
    workflowOptions,
    selectedWorkflow: selectedRuntimeImportWorkflow,
    selectedFormat: selectedRuntimeImportFormat,
  });
  const runtimeAttentionCount = sourceConnections.filter((sourceConnection) =>
    statusNeedsAttention(sourceConnection.last_collection_status || sourceConnection.status)
  ).length;
  const enabledExternalLinkCount = externalLinks.filter((definition) => definition.enabled).length;
  const enabledExportDestinationCount = exportDestinations.filter((destination) => destination.enabled).length;
  const exportAttentionCount = exportDestinations.filter((destination) => statusNeedsAttention(destination.status)).length;
  const confirmedEvaluationMappingCount = evaluationToolMappings.filter((mapping) => mapping.status === 'confirmed').length;
  const enabledEvaluationConnectionCount = evaluationToolConnections.filter((connection) => connection.enabled).length;
  const readyCredentialCount = evaluationToolConnections.filter((connection) =>
    ['configured', 'stored', 'available', 'valid'].includes(connection.credential_status)
  ).length;
  const coverageSetupReady = enabledEvaluationConnectionCount > 0 && confirmedEvaluationMappingCount > 0 && readyCredentialCount > 0;
  const createEvaluationMappingConnectionId = createEvaluationMappingForm.evaluation_tool_connection_id;
  const createEvaluationDestinationSystems = evaluationDestinationSystemsByConnection[createEvaluationMappingConnectionId] || [];
  const createEvaluationDestinationSystemsLoading = Boolean(evaluationDestinationSystemsLoadingByConnection[createEvaluationMappingConnectionId]);
  const createEvaluationDestinationSystemsError = evaluationDestinationSystemsErrorByConnection[createEvaluationMappingConnectionId] || '';
  const editingEvaluationMappingConnectionId = editingEvaluationMappingForm.evaluation_tool_connection_id;
  const editingEvaluationDestinationSystems = evaluationDestinationSystemsByConnection[editingEvaluationMappingConnectionId] || [];
  const editingEvaluationDestinationSystemsLoading = Boolean(evaluationDestinationSystemsLoadingByConnection[editingEvaluationMappingConnectionId]);
  const editingEvaluationDestinationSystemsError = evaluationDestinationSystemsErrorByConnection[editingEvaluationMappingConnectionId] || '';

  const handleReplayRuntimeSource = async (sourceConnection) => {
    if (!sourceConnection?.latest_ingestion_id) {
      return;
    }
    const actionRef = `replay:${sourceConnection.id}`;
    setRuntimeActionLoadingRef(actionRef);
    setStatusMessage('');
    try {
      const replayed = await replayIngestion(sourceConnection.latest_ingestion_id, {
        reason: 'Manual replay requested from the integrations runtime sources panel.',
      });
      setStatusMessage(`Replayed ${sourceConnection.display_name}. New ingestion ${replayed.id} is queued for processing.`);
      refresh();
    } catch (requestError) {
      setStatusMessage(requestError.message);
    } finally {
      setRuntimeActionLoadingRef(null);
    }
  };

  const handleBackfillRuntimeSource = async (sourceConnection) => {
    const actionRef = `backfill:${sourceConnection.id}`;
    setRuntimeActionLoadingRef(actionRef);
    setStatusMessage('');
    try {
      const endDate = new Date(
        sourceConnection.last_success_at || sourceConnection.evidence_posture?.last_collected_at || new Date().toISOString()
      );
      const safeEndDate = Number.isNaN(endDate.getTime()) ? new Date() : endDate;
      const windowMinutes = sourceConnection.collection_window_minutes || 60;
      const startDate = new Date(safeEndDate.getTime() - windowMinutes * 60 * 1000);
      const response = await backfillSourceConnection(sourceConnection.id, {
        start_at: startDate.toISOString(),
        end_at: safeEndDate.toISOString(),
      });
      setStatusMessage(
        `Queued ${response.created_count} ${response.created_count === 1 ? 'replay' : 'replays'} for ${sourceConnection.display_name}.`
      );
      refresh();
    } catch (requestError) {
      setStatusMessage(requestError.message);
    } finally {
      setRuntimeActionLoadingRef(null);
    }
  };

  const handleRuntimeImportSubmit = async (event) => {
    event.preventDefault();
    if (!runtimeImportWorkflowId) {
      setRuntimeImportError('Choose a workflow before uploading runtime data.');
      return;
    }
    if (!runtimeImportFile) {
      setRuntimeImportError(`Choose a ${selectedRuntimeImportFormat.label} file to upload.`);
      return;
    }

    setRuntimeImportLoading(true);
    setRuntimeImportError('');
    setStatusMessage('');
    try {
      const uploadedFile = runtimeImportFile;
      const uploadedFormatLabel = selectedRuntimeImportFormat.label;
      const formData = new FormData();
      formData.append('workflow_id', runtimeImportWorkflowId);
      formData.append('source_type', runtimeImportSourceType);
      formData.append('trace_file', uploadedFile);
      const result = await uploadRuntimeImport(selectedSystem.id, formData);
      const workflowName = workflowOptions.find((workflow) => workflow.id === runtimeImportWorkflowId)?.name || 'the selected workflow';
      const reusedSourceConnection = sourceConnections.some((sourceConnection) => sourceConnection.id === result?.source_connection_id);
      setRuntimeImportFile(null);
      setRuntimeImportFileInputKey((current) => current + 1);
      const feedback = buildRuntimeImportFeedback({
        result,
        file: uploadedFile,
        workflowName,
        formatLabel: uploadedFormatLabel,
        reusedSourceConnection,
      });
      setRuntimeImportFeedback(feedback);
      setStatusMessage({ kind: 'runtime_upload', feedback });
      refresh();
    } catch (requestError) {
      setRuntimeImportError(requestError.message);
    } finally {
      setRuntimeImportLoading(false);
    }
  };

  const submitCreateExternalLink = async () => {
    setCreateExternalLinkLoading(true);
    setCreateExternalLinkError('');
    setStatusMessage('');
    try {
      await createExternalLink(selectedSystem.id, buildExternalLinkPayload(createExternalLinkForm));
      setCreateExternalLinkForm(emptyExternalLinkForm());
      setStatusMessage('External link created.');
      refresh();
    } catch (requestError) {
      setCreateExternalLinkError(requestError.message);
    } finally {
      setCreateExternalLinkLoading(false);
    }
  };

  const submitEditExternalLink = async () => {
    if (!editingExternalLinkId) {
      return;
    }
    setEditingExternalLinkLoading(true);
    setEditingExternalLinkError('');
    setStatusMessage('');
    try {
      await updateExternalLink(editingExternalLinkId, buildExternalLinkPayload(editingExternalLinkForm, { isUpdate: true }));
      setEditingExternalLinkId(null);
      setStatusMessage('External link updated.');
      refresh();
    } catch (requestError) {
      setEditingExternalLinkError(requestError.message);
    } finally {
      setEditingExternalLinkLoading(false);
    }
  };

  const handleDeleteExternalLink = async (externalLinkId) => {
    setStatusMessage('');
    try {
      await deleteExternalLink(externalLinkId);
      setStatusMessage('External link deleted.');
      refresh();
    } catch (requestError) {
      setStatusMessage(requestError.message);
    }
  };

  const submitCreateExportDestination = async () => {
    setCreateExportDestinationLoading(true);
    setCreateExportDestinationError('');
    setStatusMessage('');
    try {
      await createExportDestination(buildExportDestinationPayload(createExportDestinationForm));
      setCreateExportDestinationForm(emptyExportDestinationForm());
      setStatusMessage('Export destination created.');
      refresh();
    } catch (requestError) {
      setCreateExportDestinationError(requestError.message);
    } finally {
      setCreateExportDestinationLoading(false);
    }
  };

  const submitEditExportDestination = async () => {
    if (!editingExportDestinationId) {
      return;
    }
    setEditingExportDestinationLoading(true);
    setEditingExportDestinationError('');
    setStatusMessage('');
    try {
      await updateExportDestination(
        editingExportDestinationId,
        buildExportDestinationPayload(editingExportDestinationForm, { includeRef: false, includeType: false })
      );
      setEditingExportDestinationId(null);
      setStatusMessage('Export destination updated.');
      refresh();
    } catch (requestError) {
      setEditingExportDestinationError(requestError.message);
    } finally {
      setEditingExportDestinationLoading(false);
    }
  };

  const toggleExportDestination = async (destination) => {
    setStatusMessage('');
    try {
      await updateExportDestination(destination.id, { enabled: !destination.enabled });
      setStatusMessage(`${destination.display_name} ${destination.enabled ? 'disabled' : 'enabled'}.`);
      refresh();
    } catch (requestError) {
      setStatusMessage(requestError.message);
    }
  };

  const submitCreateEvaluationConnection = async () => {
    setCreateEvaluationConnectionLoading(true);
    setCreateEvaluationConnectionError('');
    setStatusMessage('');
    try {
      const existingConnection = matchingEvaluationToolConnection(evaluationToolConnections, createEvaluationConnectionForm);
      if (existingConnection) {
        await updateEvaluationToolConnection(
          existingConnection.id,
          buildEvaluationToolConnectionPayload(createEvaluationConnectionForm, { includeRef: false })
        );
        setStatusMessage('Evaluation coverage connection updated.');
      } else {
        await createEvaluationToolConnection(buildEvaluationToolConnectionPayload(createEvaluationConnectionForm));
        setStatusMessage('Evaluation coverage connection created.');
      }
      setCreateEvaluationConnectionForm(emptyEvaluationToolConnectionForm());
      refresh();
    } catch (requestError) {
      setCreateEvaluationConnectionError(requestError.message);
    } finally {
      setCreateEvaluationConnectionLoading(false);
    }
  };

  const submitEditEvaluationConnection = async () => {
    if (!editingEvaluationConnectionId) {
      return;
    }
    setEditingEvaluationConnectionLoading(true);
    setEditingEvaluationConnectionError('');
    setStatusMessage('');
    try {
      await updateEvaluationToolConnection(
        editingEvaluationConnectionId,
        buildEvaluationToolConnectionPayload(editingEvaluationConnectionForm, { includeRef: false })
      );
      setEditingEvaluationConnectionId(null);
      setStatusMessage('Evaluation coverage connection updated.');
      refresh();
    } catch (requestError) {
      setEditingEvaluationConnectionError(requestError.message);
    } finally {
      setEditingEvaluationConnectionLoading(false);
    }
  };

  const submitCreateEvaluationMapping = async () => {
    setCreateEvaluationMappingLoading(true);
    setCreateEvaluationMappingError('');
    setStatusMessage('');
    try {
      await createEvaluationToolMapping(selectedSystem.id, buildEvaluationToolMappingPayload(createEvaluationMappingForm));
      setCreateEvaluationMappingForm(emptyEvaluationToolMappingForm(createEvaluationMappingForm.evaluation_tool_connection_id));
      setStatusMessage('Evaluation coverage system mapping created.');
      refresh();
    } catch (requestError) {
      setCreateEvaluationMappingError(requestError.message);
    } finally {
      setCreateEvaluationMappingLoading(false);
    }
  };

  const submitEditEvaluationMapping = async () => {
    if (!editingEvaluationMappingId) {
      return;
    }
    setEditingEvaluationMappingLoading(true);
    setEditingEvaluationMappingError('');
    setStatusMessage('');
    try {
      await updateEvaluationToolMapping(
        editingEvaluationMappingId,
        buildEvaluationToolMappingPayload(editingEvaluationMappingForm, { includeConnection: false })
      );
      setEditingEvaluationMappingId(null);
      setStatusMessage('Evaluation coverage system mapping updated.');
      refresh();
    } catch (requestError) {
      setEditingEvaluationMappingError(requestError.message);
    } finally {
      setEditingEvaluationMappingLoading(false);
    }
  };

  return (
    <section className="content-grid single-column">
      <div className="panel detail-panel">
        <div className="panel-header">
          <div>
            <p className="detail-kicker">Admin</p>
            <h2>Operator-usable connector administration</h2>
            <p className="panel-copy">Keep inbound collection, investigation links, and outbound handoff definitions separate so operators can manage them without widening the trust boundary.</p>
          </div>
          <div className="form-actions">
            <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}`}>
              Back to overview
            </a>
            <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/findings`}>
              Review findings
            </a>
            <button className="secondary-button" type="button" onClick={refresh} disabled={loading}>
              Refresh
            </button>
          </div>
        </div>

        {error ? <section className="status-card error-card">{error}</section> : null}
        {statusMessage ? (
          typeof statusMessage === 'string' ? (
            <section className="status-card">{statusMessage}</section>
          ) : statusMessage.kind === 'runtime_upload' ? (
            <RuntimeUploadStatusCard feedback={statusMessage.feedback} />
          ) : null
        ) : null}
        {loading ? <section className="status-card">Loading integrations…</section> : null}

        {!loading ? (
          <div className="stack">
            <div className="findings-summary-grid">
              <div className="detail-card summary-card">
                <span className="summary-card-label">Runtime connections</span>
                <strong>{sourceConnections.length}</strong>
              </div>
              <div className="detail-card summary-card">
                <span className="summary-card-label">Runtime attention</span>
                <strong>{runtimeAttentionCount}</strong>
              </div>
              <div className="detail-card summary-card">
                <span className="summary-card-label">Enabled links</span>
                <strong>{enabledExternalLinkCount}</strong>
              </div>
              <div className="detail-card summary-card">
                <span className="summary-card-label">Enabled handoffs</span>
                <strong>{enabledExportDestinationCount}</strong>
              </div>
              <div className="detail-card summary-card">
                <span className="summary-card-label">Evaluation mappings</span>
                <strong>{confirmedEvaluationMappingCount}</strong>
              </div>
              <div className="detail-card summary-card summary-card-muted summary-card-access">
                <span className="summary-card-label">Current access</span>
                <strong>{accessLabel}</strong>
              </div>
            </div>

            <section className="findings-priority-band">
              <div className="chapter-header">
                <div>
                  <p className="chapter-kicker">Connector families</p>
                  <h3>Four integration families</h3>
                </div>
              </div>
              <p className="panel-copy">
                Runtime source connections are inbound. External links are deterministic investigation shortcuts. Evaluation coverage mappings connect reviewed runtime risk to ASE. Export destinations remain issue-tracker handoff targets.
              </p>
              <p className="overview-note">
                {canManageIntegrations
                  ? 'Tenant admins can change definitions and destination configuration.'
                  : canOperateRuntimeSources
                    ? 'Operators can append or replay runtime evidence, but connector definitions remain read-only.'
                    : 'This view is read-only for your current role.'}
              </p>
              {runtimeAttentionCount || exportAttentionCount ? (
                <p className="overview-note">
                  {runtimeAttentionCount} runtime {runtimeAttentionCount === 1 ? 'source needs' : 'sources need'} attention and {exportAttentionCount}{' '}
                  export {exportAttentionCount === 1 ? 'destination needs' : 'destinations need'} attention.
                </p>
              ) : null}
            </section>

            <section className="detail-section">
              <div className="chapter-header">
                <div>
                  <p className="chapter-kicker">Evaluation coverage</p>
                  <h3>Automated-System-Eval connections</h3>
                </div>
                <div className="chip-row">
                  <span className="chip">{evaluationToolConnections.length} connections</span>
                  <span className="chip">{evaluationToolMappings.length} mappings</span>
                </div>
              </div>
              <p className="panel-copy">Confirmed mappings keep the runtime review send path explicit: Agentic Blue tenant and system records stay separate from ASE workspace and system records.</p>
              <article className="detail-card coverage-setup-panel" aria-label="Coverage setup summary">
                <div className="workflow-mode-header">
                  <div>
                    <span className="summary-card-label">Coverage setup</span>
                    <strong>{coverageSetupReady ? 'Ready for reviewed runtime-risk sends' : 'Setup needed before coverage sends'}</strong>
                    <p>
                      Agentic Blue sends only reviewed runtime-risk suggestions to the mapped Automated-System-Eval workspace and system.
                    </p>
                  </div>
                  <StageStatusBadge status={coverageSetupReady ? 'ready' : 'attention'}>
                    {coverageSetupReady ? 'Ready' : 'Needs setup'}
                  </StageStatusBadge>
                </div>
                <div className="coverage-setup-grid">
                  <div className="coverage-setup-item">
                    <span>Enabled ASE connections</span>
                    <strong>{enabledEvaluationConnectionCount}</strong>
                  </div>
                  <div className="coverage-setup-item">
                    <span>Confirmed mappings</span>
                    <strong>{confirmedEvaluationMappingCount}</strong>
                  </div>
                  <div className="coverage-setup-item">
                    <span>Credentials</span>
                    <strong>{readyCredentialCount ? `${readyCredentialCount} configured` : 'Needs credential'}</strong>
                  </div>
                  <div className="coverage-setup-item">
                    <span>Destination systems</span>
                    <strong>{evaluationToolMappings.length ? 'Selected from ASE' : 'Not mapped'}</strong>
                  </div>
                </div>
              </article>
              {canManageIntegrations ? (
                <EvaluationToolConnectionEditor
                  title="New ASE connection"
                  form={createEvaluationConnectionForm}
                  setForm={setCreateEvaluationConnectionForm}
                  onSubmit={submitCreateEvaluationConnection}
                  submitLabel="Create ASE connection"
                  loading={createEvaluationConnectionLoading}
                  error={createEvaluationConnectionError}
                />
              ) : null}
              <div className="stack compact-stack">
                {evaluationToolConnections.length ? (
                  evaluationToolConnections.map((connection) => (
                    <article className="detail-card" key={connection.id}>
                      <div className="overview-section-header">
                        <div>
                          <strong>{connection.display_name}</strong>
                          <p>
                            {connection.ase_workspace_name || connection.ase_workspace_id} · {connection.environment_label || 'environment not labelled'}
                          </p>
                        </div>
                        <div className="chip-row">
                          <span className={chipToneClass(connection.status)}>{connection.status}</span>
                          <span className="chip">Credentials {connection.credential_status}</span>
                          <span className={onOffChipClass(connection.enabled)}>{connection.enabled ? 'enabled' : 'disabled'}</span>
                        </div>
                      </div>
                      <div className="labeled-stat-list">
                        <div className="labeled-stat-row">
                          <span>Workspace</span>
                          <strong>{connection.ase_workspace_id}</strong>
                        </div>
                        <div className="labeled-stat-row">
                          <span>Allowed systems</span>
                          <strong>{connection.allowed_destination_systems?.length || 'Any mapped system'}</strong>
                        </div>
                        <div className="labeled-stat-row">
                          <span>Last verified</span>
                          <strong>{formatTimestamp(connection.last_verified_at)}</strong>
                        </div>
                      </div>
                      {canManageIntegrations ? (
                        <div className="form-actions">
                          <button
                            className="secondary-button"
                            type="button"
                            onClick={() => {
                              setEditingEvaluationConnectionId(connection.id);
                              setEditingEvaluationConnectionForm(evaluationConnectionFormFromConnection(connection));
                              setEditingEvaluationConnectionError('');
                            }}
                          >
                            Edit
                          </button>
                        </div>
                      ) : null}
                      {editingEvaluationConnectionId === connection.id ? (
                        <EvaluationToolConnectionEditor
                          title={`Edit ${connection.display_name}`}
                          form={editingEvaluationConnectionForm}
                          setForm={setEditingEvaluationConnectionForm}
                          onSubmit={submitEditEvaluationConnection}
                          submitLabel="Save ASE connection"
                          loading={editingEvaluationConnectionLoading}
                          error={editingEvaluationConnectionError}
                          readOnlyRef
                        />
                      ) : null}
                    </article>
                  ))
                ) : null}
              </div>

              {canManageIntegrations && hasEvaluationToolConnections ? (
                <EvaluationToolMappingEditor
                  title="Map this system to ASE"
                  form={createEvaluationMappingForm}
                  setForm={setCreateEvaluationMappingForm}
                  connections={evaluationToolConnections}
                  destinationSystems={createEvaluationDestinationSystems}
                  destinationSystemsLoading={createEvaluationDestinationSystemsLoading}
                  destinationSystemsError={createEvaluationDestinationSystemsError}
                  onRefreshSystems={() => loadEvaluationDestinationSystems(createEvaluationMappingConnectionId)}
                  onSubmit={submitCreateEvaluationMapping}
                  submitLabel="Create system mapping"
                  loading={createEvaluationMappingLoading}
                  error={createEvaluationMappingError}
                />
              ) : null}
              {hasEvaluationToolConnections ? (
                <div className="stack compact-stack">
                  {evaluationToolMappings.length ? (
                  evaluationToolMappings.map((mapping) => (
                    <article className="detail-card" key={mapping.id}>
                      <div className="overview-section-header">
                        <div>
                          <strong>{mapping.ase_system_display_name}</strong>
                          <p>
                            {mapping.connection_display_name} · {mapping.ase_workspace_name || mapping.ase_workspace_id}
                          </p>
                        </div>
                        <div className="chip-row">
                          <span className={chipToneClass(mapping.status)}>{mapping.status}</span>
                          <span className="chip">{mapping.confidence}</span>
                        </div>
                      </div>
                      <div className="labeled-stat-list">
                        <div className="labeled-stat-row">
                          <span>ASE system</span>
                          <strong>{mapping.ase_system_id}</strong>
                        </div>
                        <div className="labeled-stat-row">
                          <span>Last verified</span>
                          <strong>{formatTimestamp(mapping.last_verified_at)}</strong>
                        </div>
                      </div>
                      {canManageIntegrations ? (
                        <div className="form-actions">
                          <button
                            className="secondary-button"
                            type="button"
                            onClick={() => {
                              setEditingEvaluationMappingId(mapping.id);
                              setEditingEvaluationMappingForm(evaluationMappingFormFromMapping(mapping));
                              setEditingEvaluationMappingError('');
                            }}
                          >
                            Edit
                          </button>
                        </div>
                      ) : null}
                      {editingEvaluationMappingId === mapping.id ? (
                        <EvaluationToolMappingEditor
                          title={`Edit ${mapping.ase_system_display_name}`}
                          form={editingEvaluationMappingForm}
                          setForm={setEditingEvaluationMappingForm}
                          connections={evaluationToolConnections}
                          destinationSystems={editingEvaluationDestinationSystems}
                          destinationSystemsLoading={editingEvaluationDestinationSystemsLoading}
                          destinationSystemsError={editingEvaluationDestinationSystemsError}
                          onRefreshSystems={() => loadEvaluationDestinationSystems(editingEvaluationMappingConnectionId)}
                          onSubmit={submitEditEvaluationMapping}
                          submitLabel="Save system mapping"
                          loading={editingEvaluationMappingLoading}
                          error={editingEvaluationMappingError}
                          readOnlyConnection
                        />
                      ) : null}
                    </article>
                  ))
                  ) : (
                    <div className="empty-state-card">
                      <strong>No ASE system mapping for this system</strong>
                      <p>Runtime review sends stay blocked until an explicit ASE system mapping is confirmed.</p>
                    </div>
                  )}
                </div>
              ) : null}
            </section>

            <section className="detail-section">
              <div className="chapter-header">
                <div>
                  <p className="chapter-kicker">Inbound</p>
                  <h3>Runtime source connections</h3>
                </div>
                <span className="chip">{sourceConnections.length} connections</span>
              </div>
              <p className="panel-copy">Inbound source connections stay system-scoped. Uploading another trace file usually appends to an existing targeted connection for that format, so this count changes only when a new source family is added.</p>
              {canOperateRuntimeSources ? (
                <form className="form-section" onSubmit={handleRuntimeImportSubmit}>
                  <div className="form-section-header">
                    <h3>Add runtime data</h3>
                    <p>Append one supported runtime trace to an existing workflow. Smart analysis is the default; explicit source families are available as an operator override.</p>
                  </div>
                  <div className="access-grid">
                    <label className="form-field">
                      <span>Workflow</span>
                      <select
                        className="text-input"
                        value={runtimeImportWorkflowId}
                        onChange={(event) => setRuntimeImportWorkflowId(event.target.value)}
                        disabled={!workflowOptions.length || runtimeImportLoading}
                      >
                        {workflowOptions.length ? null : <option value="">No workflows available</option>}
                        {workflowOptions.map((workflow) => (
                          <option key={workflow.id} value={workflow.id}>
                            {workflow.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <DisclosurePanel title="Advanced format override" summaryMeta={selectedRuntimeImportFormat.label}>
                    <label className="form-field">
                      <span>Format</span>
                      <select
                        className="text-input"
                        value={runtimeImportSourceType}
                        onChange={(event) => {
                          setRuntimeImportSourceType(event.target.value);
                          setRuntimeImportFile(null);
                          setRuntimeImportFileInputKey((current) => current + 1);
                        }}
                        disabled={runtimeImportLoading}
                      >
                        {RUNTIME_IMPORT_FORMAT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </DisclosurePanel>
                  <div className="form-field">
                    <span>Trace file</span>
                    <label className={runtimeImportFile ? 'runtime-file-picker has-file' : 'runtime-file-picker'}>
                      <input
                        key={`${runtimeImportSourceType}-${runtimeImportFileInputKey}`}
                        aria-label="Trace file"
                        className="runtime-file-picker-input"
                        type="file"
                        accept={selectedRuntimeImportFormat.accept}
                        onChange={(event) => {
                          setRuntimeImportFile(event.target.files?.[0] || null);
                          setRuntimeImportFeedback(null);
                        }}
                        disabled={runtimeImportLoading}
                      />
                      <span className="runtime-file-picker-icon" aria-hidden="true">+</span>
                      <span className="runtime-file-picker-copy">
                        <strong>{runtimeImportFile ? runtimeImportFile.name : 'Choose trace file'}</strong>
                        <small>{runtimeImportFile ? 'Ready for trace-shape analysis' : 'JSON and JSONL files are analysed before append.'}</small>
                      </span>
                      <span className="chip">{selectedRuntimeImportFormat.label}</span>
                    </label>
                  </div>
                  <section className="trace-analysis-preview" aria-label="Smart trace analysis flow">
                    <div className="trace-analysis-preview-copy">
                      <p className="detail-kicker">Analysis flow</p>
                      <h4>Upload, map, then ingest</h4>
                      <p>Agentic Blue reads the trace structure first, maps high-confidence runtime evidence, and keeps lower-confidence guidance visible.</p>
                    </div>
                    <TraceAnalysisStepper phase={runtimeImportLoading ? 'analysing' : runtimeImportFeedback ? 'complete' : runtimeImportFile ? 'ready' : 'idle'} />
                  </section>
                  {runtimeImportFeedback ? (
                    <section className="runtime-upload-feedback runtime-upload-feedback-success" role="status" aria-label="Runtime upload result">
                      <div className="runtime-upload-feedback-header">
                        <div>
                          <p className="detail-kicker">{runtimeImportFeedback.deduped ? 'Existing evidence reused' : 'Runtime evidence added'}</p>
                          <h4>{runtimeImportFeedback.title}</h4>
                        </div>
                        <span className="chip chip-success">{runtimeImportFeedback.status || 'registered'}</span>
                      </div>
                      <div className="runtime-upload-feedback-grid">
                        <div>
                          <span>File</span>
                          <strong>{runtimeImportFeedback.fileName}</strong>
                        </div>
                        <div>
                          <span>Uploaded</span>
                          <strong>{formatTimestamp(runtimeImportFeedback.uploadedAt)}</strong>
                        </div>
                        <div>
                          <span>Workflow</span>
                          <strong>{runtimeImportFeedback.workflowName}</strong>
                        </div>
                        <div>
                          <span>Format</span>
                          <strong>{runtimeImportFeedback.detectedFormat || runtimeImportFeedback.formatLabel}</strong>
                        </div>
                        {runtimeImportFeedback.detectedVariant ? (
                          <div>
                            <span>Variant</span>
                            <strong>{runtimeImportFeedback.detectedVariant}</strong>
                          </div>
                        ) : null}
                        {typeof runtimeImportFeedback.mappingConfidence === 'number' ? (
                          <div>
                            <span>Mapping confidence</span>
                            <strong>{Math.round(runtimeImportFeedback.mappingConfidence * 100)}%</strong>
                          </div>
                        ) : null}
                      </div>
                      <TraceAnalysisSummary
                        summary={runtimeImportFeedback.analysisSummary}
                        mappingConfidence={runtimeImportFeedback.mappingConfidence}
                        semanticProfile={runtimeImportFeedback.semanticProfile}
                        guidanceWarnings={runtimeImportFeedback.guidanceWarnings}
                      />
                      {runtimeImportFeedback.ingestionId ? (
                        <DisclosurePanel title="Upload details" summaryMeta="Technical reference">
                          <div className="labeled-stat-row">
                            <span>Ingestion</span>
                            <strong>{runtimeImportFeedback.ingestionId}</strong>
                          </div>
                          {runtimeImportFeedback.semanticProfile ? (
                            <div className="labeled-stat-row">
                              <span>Semantic profile</span>
                              <strong>{runtimeImportFeedback.semanticProfile}</strong>
                            </div>
                          ) : null}
                        </DisclosurePanel>
                      ) : null}
                    </section>
                  ) : null}
                  <p className="overview-note">This append path adds primary evaluation evidence only. Historical baseline uploads stay out of scope here.</p>
                  <DisclosurePanel title="Follow-on evidence" className="runtime-upload-guidance-disclosure" summaryMeta="Trace review">
                    <EvidenceNeededGuidanceCard
                      title="Trace review guidance"
                      canSay={runtimeUploadGuidance.canSay}
                      cannotSay={runtimeUploadGuidance.cannotSay}
                      nextSteps={runtimeUploadGuidance.nextSteps}
                      framed={false}
                    />
                  </DisclosurePanel>
                  {runtimeImportError ? <p>{runtimeImportError}</p> : null}
                  <div className="form-actions">
                    <button
                      className="primary-button"
                      type="submit"
                      disabled={runtimeImportLoading || !workflowOptions.length}
                    >
                      {runtimeImportLoading ? 'Uploading…' : 'Upload trace file'}
                    </button>
                  </div>
                </form>
              ) : null}
              <div className="stack compact-stack">
                {sourceConnections.length ? (
                  sourceConnections.map((sourceConnection) => (
                    <article className="detail-card" key={sourceConnection.id}>
                      <div className="overview-section-header">
                        <div>
                          <strong>{sourceConnection.display_name}</strong>
                          <p>
                            {sourceTypeLabel(sourceConnection.source_type)} · {sourceConnection.connection_mode}
                          </p>
                        </div>
                        <div className="chip-row">
                          <span className={chipToneClass(sourceConnection.last_collection_status || sourceConnection.status)}>
                            {sourceConnection.last_collection_status || sourceConnection.status}
                          </span>
                          <span className="chip">Credentials {sourceConnection.credential_status}</span>
                          {sourceConnection.evidence_posture?.freshness_state ? (
                            <span className="chip">Freshness {sourceConnection.evidence_posture.freshness_state}</span>
                          ) : null}
                        </div>
                      </div>
                      <div className="labeled-stat-list">
                        <div className="labeled-stat-row">
                          <span>Last success</span>
                          <strong>{formatTimestamp(sourceConnection.last_success_at)}</strong>
                        </div>
                        <div className="labeled-stat-row">
                          <span>Expected interval</span>
                          <strong>{sourceConnection.expected_interval_minutes ? `${sourceConnection.expected_interval_minutes} min` : 'Not configured'}</strong>
                        </div>
                      </div>
                      <DisclosurePanel title="Runtime source details" className="runtime-source-technical-details">
                        <div className="labeled-stat-list">
                          <div className="labeled-stat-row">
                            <span>Last failure</span>
                            <strong>{formatTimestamp(sourceConnection.last_failure_at)}</strong>
                          </div>
                          <div className="labeled-stat-row">
                            <span>Latest ingestion</span>
                            <strong>{sourceConnection.latest_ingestion_id || 'Not available yet'}</strong>
                          </div>
                          <div className="labeled-stat-row">
                            <span>Replay window</span>
                            <strong>{runtimeSourceWindowLabel(sourceConnection)}</strong>
                          </div>
                        </div>
                        {sourceConnection.secrets_visible && sourceConnection.secret_ref ? (
                          <p className="overview-note">
                            Secret reference: {formatSecretReference(sourceConnection.secret_provider, sourceConnection.secret_ref)}
                          </p>
                        ) : null}
                      </DisclosurePanel>
                      {sourceConnection.last_error_message ? <p className="overview-note">{sourceConnection.last_error_message}</p> : null}
                      {canOperateRuntimeSources ? (
                        <div className="form-actions">
                          <button
                            className="secondary-button"
                            type="button"
                            disabled={runtimeActionLoadingRef === `replay:${sourceConnection.id}` || !sourceConnection.latest_ingestion_id}
                            onClick={() => handleReplayRuntimeSource(sourceConnection)}
                          >
                            {runtimeActionLoadingRef === `replay:${sourceConnection.id}` ? 'Replaying…' : 'Replay latest'}
                          </button>
                          <button
                            className="secondary-button"
                            type="button"
                            disabled={runtimeActionLoadingRef === `backfill:${sourceConnection.id}`}
                            onClick={() => handleBackfillRuntimeSource(sourceConnection)}
                          >
                            {runtimeActionLoadingRef === `backfill:${sourceConnection.id}`
                              ? 'Replaying window…'
                              : `Replay ${runtimeSourceWindowLabel(sourceConnection)}`}
                          </button>
                        </div>
                      ) : null}
                    </article>
                  ))
                ) : (
                  <div className="inline-info-note">
                    <strong>No runtime source connections configured yet</strong>
                    <p>Push-based collection and freshness tracking remain available once a source connection has been created.</p>
                  </div>
                )}
              </div>
            </section>

            <section className="detail-section">
              <div className="chapter-header">
                <div>
                  <p className="chapter-kicker">Investigate</p>
                  <h3>External links</h3>
                </div>
                <span className="chip">{externalLinks.length} configured</span>
              </div>
              <p className="panel-copy">External links are system-scoped definition records. They never store secrets or perform live vendor health checks.</p>
              {canManageIntegrations ? (
                <DisclosurePanel title="New external link" className="integration-create-disclosure" summaryMeta="Add link">
                  <ExternalLinkEditor
                    title="Link details"
                    form={createExternalLinkForm}
                    setForm={setCreateExternalLinkForm}
                    onSubmit={submitCreateExternalLink}
                    submitLabel="Create external link"
                    loading={createExternalLinkLoading}
                    error={createExternalLinkError}
                  />
                </DisclosurePanel>
              ) : null}
              <div className="stack compact-stack">
                {externalLinks.length ? (
                  externalLinks.map((definition) => (
                    <article className="detail-card" key={definition.id}>
                      <div className="overview-section-header">
                        <div>
                          <strong>{definition.display_name}</strong>
                          <p>
                            {definition.provider} · {definition.link_kind} · {definition.target_type}
                          </p>
                        </div>
                        <div className="chip-row">
                          <span className={chipToneClass(definition.enabled ? 'available' : 'disabled')}>
                            {definition.enabled ? 'enabled' : 'disabled'}
                          </span>
                          <span className="chip">{definition.resolution_mode}</span>
                          <span className="chip">{definition.metadata?.preset_mode === 'provider_preset' ? 'Preset-backed' : 'Custom'}</span>
                        </div>
                      </div>
                      <div className="labeled-stat-list">
                        <div className="labeled-stat-row">
                          <span>Definition mode</span>
                          <strong>{definition.metadata?.preset_mode === 'provider_preset' ? 'Provider preset' : 'Custom link'}</strong>
                        </div>
                        <div className="labeled-stat-row">
                          <span>Target type</span>
                          <strong>{definition.target_type}</strong>
                        </div>
                        <div className="labeled-stat-row">
                          <span>Link kind</span>
                          <strong>{definition.link_kind}</strong>
                        </div>
                      </div>
                      <p className="overview-note">
                        {definition.url_value || definition.metadata?.base_url || 'Preset-backed link resolves from persisted system context.'}
                      </p>
                      {definition.metadata?.required_variables?.length ? (
                        <p className="panel-copy">Required variables: {definition.metadata.required_variables.join(', ')}</p>
                      ) : null}
                      {definition.metadata?.preset_mode ? <p className="panel-copy">Mode: {definition.metadata.preset_mode}</p> : null}
                      {canManageIntegrations ? (
                        <div className="form-actions">
                          <button
                            className="secondary-button"
                            type="button"
                            onClick={() => {
                              setEditingExternalLinkId(definition.id);
                              setEditingExternalLinkForm(externalLinkFormFromDefinition(definition));
                              setEditingExternalLinkError('');
                            }}
                          >
                            Edit
                          </button>
                          <button className="secondary-button" type="button" onClick={() => handleDeleteExternalLink(definition.id)}>
                            Delete
                          </button>
                        </div>
                      ) : null}
                      {editingExternalLinkId === definition.id ? (
                        <ExternalLinkEditor
                          title={`Edit ${definition.display_name}`}
                          form={editingExternalLinkForm}
                          setForm={setEditingExternalLinkForm}
                          onSubmit={submitEditExternalLink}
                          submitLabel="Save external link"
                          loading={editingExternalLinkLoading}
                          error={editingExternalLinkError}
                        />
                      ) : null}
                    </article>
                  ))
                ) : null}
              </div>
            </section>

            <section className="detail-section">
              <div className="chapter-header">
                <div>
                  <p className="chapter-kicker">Outbound</p>
                  <h3>Export destinations</h3>
                </div>
                <span className="chip">{exportDestinations.length} managed</span>
              </div>
              <p className="panel-copy">Outbound export destinations are tenant-scoped and reusable across systems. Managed destinations take precedence over any legacy environment fallback with the same reference.</p>
              {canManageIntegrations ? (
                <DisclosurePanel title="New export destination" className="integration-create-disclosure" summaryMeta="Add destination">
                  <ExportDestinationEditor
                    title="Destination details"
                    form={createExportDestinationForm}
                    setForm={setCreateExportDestinationForm}
                    onSubmit={submitCreateExportDestination}
                    submitLabel="Create export destination"
                    loading={createExportDestinationLoading}
                    error={createExportDestinationError}
                  />
                </DisclosurePanel>
              ) : null}
              <div className="stack compact-stack">
                {exportDestinations.length ? (
                  exportDestinations.map((destination) => (
                    <article className="detail-card" key={destination.id || destination.destination_ref}>
                      <div className="overview-section-header">
                        <div>
                          <strong>{destination.display_name}</strong>
                          <p>
                            {destination.destination_type} · {destination.destination_ref}
                          </p>
                        </div>
                        <div className="chip-row">
                          <span className={chipToneClass(destination.status)}>{destination.status}</span>
                          <span className={onOffChipClass(destination.enabled)}>{destination.enabled ? 'enabled' : 'disabled'}</span>
                          <span className="chip">Credentials {destination.credential_status}</span>
                        </div>
                      </div>
                      <div className="labeled-stat-list">
                        <div className="labeled-stat-row">
                          <span>Last success</span>
                          <strong>{formatTimestamp(destination.last_success_at)}</strong>
                        </div>
                        <div className="labeled-stat-row">
                          <span>Last failure</span>
                          <strong>{formatTimestamp(destination.last_failure_at)}</strong>
                        </div>
                        <div className="labeled-stat-row">
                          <span>Jira project</span>
                          <strong>{destination.project_key || 'Not configured'}</strong>
                        </div>
                        <div className="labeled-stat-row">
                          <span>Issue type</span>
                          <strong>{destination.issue_type || 'Not configured'}</strong>
                        </div>
                        <div className="labeled-stat-row">
                          <span>Base URL</span>
                          <strong>{destination.base_url || 'Not configured'}</strong>
                        </div>
                      </div>
                      {destination.secrets_visible && destination.secret_ref ? (
                        <p className="overview-note">
                          Secret reference: {formatSecretReference(destination.secret_provider, destination.secret_ref)}
                        </p>
                      ) : null}
                      {destination.last_error_message ? <p className="overview-note">{destination.last_error_message}</p> : null}
                      {canManageIntegrations && destination.id ? (
                        <div className="form-actions">
                          <button
                            className="secondary-button"
                            type="button"
                            onClick={() => {
                              setEditingExportDestinationId(destination.id);
                              setEditingExportDestinationForm(exportDestinationFormFromDestination(destination));
                              setEditingExportDestinationError('');
                            }}
                          >
                            Edit
                          </button>
                          <button className="secondary-button" type="button" onClick={() => toggleExportDestination(destination)}>
                            {destination.enabled ? 'Disable' : 'Enable'}
                          </button>
                        </div>
                      ) : null}
                      {editingExportDestinationId === destination.id ? (
                        <ExportDestinationEditor
                          title={`Edit ${destination.display_name}`}
                          form={editingExportDestinationForm}
                          setForm={setEditingExportDestinationForm}
                          onSubmit={submitEditExportDestination}
                          submitLabel="Save export destination"
                          loading={editingExportDestinationLoading}
                          error={editingExportDestinationError}
                        />
                      ) : null}
                    </article>
                  ))
                ) : null}
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </section>
  );
}
